/**
 * Telemetria leve do app (Sprint I §2.2 + §2.3).
 *
 * Objetivo: ter um único ponto de entrada para emitir eventos analíticos sem
 * acoplar o app a um SDK específico (Firebase Analytics, Amplitude, Mixpanel
 * etc.). Hoje só fazemos `console.debug` em dev e disparamos um
 * CustomEvent global, o que permite plugar um collector futuramente sem
 * tocar nos call-sites.
 *
 * Convenções:
 *  - Nomes em snake_case com prefixo de domínio: `share_card_*`, `ai_*`,
 *    `league_challenge_*` etc.
 *  - Props são serializáveis (string | number | boolean | null) e devem
 *    descrever a ação, não o estado completo.
 *  - Sem PII: nunca enviar o conteúdo de mensagens, capas reais, e-mails ou
 *    fotos. Apenas tamanhos, tipos e contadores.
 */

export type TelemetryProp = string | number | boolean | null;
export type TelemetryProps = Record<string, TelemetryProp>;

const TELEMETRY_EVENT_NAME = "lura:telemetry";

function isProductionBuild(): boolean {
  try {
    return import.meta.env?.PROD === true;
  } catch {
    return false;
  }
}

/**
 * Emite um evento de telemetria. Em dev, faz `console.debug` para inspeção
 * rápida. Sempre emite um `CustomEvent` global para futuros collectors.
 */
export function track(event: string, props: TelemetryProps = {}): void {
  if (!event || typeof event !== "string") return;

  const payload = {event, props, ts: Date.now()};

  if (!isProductionBuild()) {
    console.debug("[Lura/telemetry]", event, props);
  }

  try {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(
        new CustomEvent(TELEMETRY_EVENT_NAME, {detail: payload}),
      );
    }
  } catch {
    /* ignore (SSR / older browsers) */
  }
}

export const TELEMETRY_EVENT_BUS_NAME = TELEMETRY_EVENT_NAME;

/**
 * Emite o evento UMA única vez por device (dedup via localStorage) — para os
 * marcos de ativação do funil D0 (`first_book_added`, `first_progress_logged`).
 * Best-effort: sem storage disponível, emite sempre (o relatório deduplica
 * por uid de qualquer forma).
 */
export function trackOnce(event: string, props: TelemetryProps = {}): void {
  const key = `livra_evt_once_${event}`;
  try {
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
  } catch {
    /* storage indisponível — segue emitindo */
  }
  track(event, props);
}

/**
 * Emite o evento UMA vez por `scopeKey` dentro da SESSÃO do navegador
 * (`sessionStorage`) — para eventos de impressão/visualização que servem de
 * DENOMINADOR (`book_detail_view`, `affiliate_impression`): rerender, troca de
 * aba e voltar-e-abrir-de-novo não podem contar como vista nova, senão o CTR
 * sai deflacionado. Fechou a aba, conta de novo — é uma sessão nova mesmo.
 *
 * Best-effort como o `trackOnce`: sem storage, emite sempre.
 */
export function trackOncePerSession(
  event: string,
  scopeKey: string,
  props: TelemetryProps = {},
): void {
  const key = `livra_evt_sess_${event}_${scopeKey}`;
  try {
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
  } catch {
    /* storage indisponível — segue emitindo */
  }
  track(event, props);
}
